import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { PageHeader, Toolbar, DataCard, StatusPill } from "@/components/admin/AdminPrimitives";
import { Skeleton } from "@/components/ui/skeleton";
import { UserPlus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/usuarios")({
  head: () => ({ meta: [{ title: "Usuários — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: UsuariosAdmin,
});

function UsuariosAdmin() {
  const [q, setQ] = useState("");
  const list = useQuery({
    queryKey: ["admin.usuarios"],
    queryFn: async () => {
      const [profiles, ents] = await Promise.all([
        supabase.from("profiles").select("id, full_name, email, created_at").order("created_at", { ascending: false }).limit(500),
        supabase.from("entitlements").select("user_id, status").eq("status", "active"),
      ]);
      const activeIds = new Set((ents.data ?? []).map((e: any) => e.user_id));
      return (profiles.data ?? []).map((p: any) => ({ ...p, active: activeIds.has(p.id) }));
    },
  });

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list.data ?? [];
    return (list.data ?? []).filter((r: any) =>
      [r.full_name, r.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)),
    );
  }, [q, list.data]);

  return (
    <div>
      <PageHeader
        title="Usuários"
        description="Base completa de contas cadastradas."
        actions={
          <button className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--navy)] px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
            <UserPlus size={14} /> Conceder acesso
          </button>
        }
      />
      <Toolbar search={q} onSearch={setQ} placeholder="Pesquisar por nome ou e-mail…" />
      <DataCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--gray-soft)]/60 text-left text-[11px] uppercase tracking-wide text-[var(--gray-ink)]">
              <tr>
                <th className="px-4 py-3 font-semibold">Usuário</th>
                <th className="px-4 py-3 font-semibold">E-mail</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Cadastro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.05]">
              {list.isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}><td colSpan={4} className="p-2"><Skeleton className="h-9 w-full" /></td></tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="py-16 text-center text-sm text-[var(--gray-ink)]">Nenhum usuário encontrado.</td></tr>
              ) : (
                rows.map((u: any) => (
                  <tr key={u.id} className="transition hover:bg-[var(--gray-soft)]/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--navy)] text-xs font-semibold text-white">
                          {(u.full_name || u.email || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <span className="font-medium text-[var(--navy)]">{u.full_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[var(--gray-ink)]">{u.email ?? "—"}</td>
                    <td className="px-4 py-3">
                      <StatusPill tone={u.active ? "green" : "gray"}>{u.active ? "Ativo" : "Sem acesso"}</StatusPill>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--gray-ink)]">
                      {new Date(u.created_at).toLocaleDateString("pt-BR")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DataCard>
    </div>
  );
}
